import api from './api';

export interface ExportOptions {
  sensorId: number;
  startTime?: string;
  endTime?: string;
  fields?: string[];
}

export interface ComparisonExportOptions {
  sensorIds: number[];
  startTime?: string;
  endTime?: string;
  fields?: string[];
}

export const exportService = {
  /**
   * Export sensor readings to Excel file
   */
  async exportSensorReadings(options: ExportOptions): Promise<void> {
    const params = new URLSearchParams();
    
    if (options.startTime) {
      params.append('start_time', options.startTime);
    }
    
    if (options.endTime) {
      params.append('end_time', options.endTime);
    }
    
    if (options.fields && options.fields.length > 0) {
      params.append('fields', options.fields.join(','));
    }
    
    const queryString = params.toString();
    const url = `/api/sensors/${options.sensorId}/export${queryString ? `?${queryString}` : ''}`;
    
    try {
      const response = await api.get(url, {
        responseType: 'blob',
      });
      
      // Create blob from response
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      
      // Extract filename from content-disposition header if available
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'sensor_readings.xlsx';
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=(.+)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      
      // Create download link
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Export failed:', error);
      throw error;
    }
  },

  /**
   * Export comparison data for multiple sensors to Excel file
   */
  async exportComparisonData(options: ComparisonExportOptions): Promise<void> {
    const params = new URLSearchParams();
    
    // Add sensor IDs
    params.append('sensor_ids', options.sensorIds.join(','));
    
    if (options.startTime) {
      params.append('start_time', options.startTime);
    }
    
    if (options.endTime) {
      params.append('end_time', options.endTime);
    }
    
    if (options.fields && options.fields.length > 0) {
      params.append('fields', options.fields.join(','));
    }
    
    const url = `/api/sensors/export-comparison?${params.toString()}`;
    
    try {
      const response = await api.get(url, {
        responseType: 'blob',
      });
      
      // Create blob from response
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      
      // Extract filename from content-disposition header if available
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'sensor_comparison.xlsx';
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=(.+)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      
      // Create download link
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Comparison export failed:', error);
      throw error;
    }
  },
};

